import { forwardRef, useCallback } from 'react';
import styles from './index.module.scss';
import { useSnackbar, SnackbarContent, CustomContentProps } from 'notistack';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import Error from '@/assets/notifications/error.svg';
import Success from '@/assets/notifications/success.svg';
import Warning from '@/assets/notifications/warning.svg';
import Info from '@/assets/notifications/info.svg';
import CloseIcon from '@mui/icons-material/Close';
import { RotatingLines } from 'react-loader-spinner';
import clsx from 'clsx';

interface NotificationProps extends CustomContentProps {
  type?: 'success' | 'warning' | 'info' | 'error' | 'loading';
}

const SUCCESS = 'success';
const INFO = 'info';
const INFO_TYPES = ['info', 'loading'];
const GENERAL_TYPES = ['success', 'warning', 'error', 'info'];

const NotificationSnackBar = forwardRef<HTMLDivElement, NotificationProps>(({ id, type = 'success', message }, ref) => {
  const { closeSnackbar } = useSnackbar();

  const handleDismiss = useCallback(() => {
    closeSnackbar(id);
  }, [id, closeSnackbar]);

  const selectIcon = () => {
    switch (type) {
      case 'info':
        return Info;
      case 'success':
        return Success;
      case 'warning':
        return Warning;
      case 'error':
        return Error;
      default:
        return Success;
    }
  };

  const prepareIcon = () => {
    return type === 'loading' ? (
      <div className='mx-4'>
        <RotatingLines strokeColor='black' strokeWidth='' animationDuration='0.75' width='24' visible={true} />
      </div>
    ) : (
      <img src={selectIcon()} alt={'success'} className='ml-2 mr-4 w-6 h-6' />
    );
  };

  return (
    <SnackbarContent ref={ref}>
      <Card className={styles[type || SUCCESS]}>
        <CardActions>
          {prepareIcon()}
          <Typography className={clsx('whitespace-pre-wrap', INFO_TYPES.includes(type) ? styles.infoTypography : styles.typography)}>
            {message}
          </Typography>
          {GENERAL_TYPES.includes(type) && (
            <CloseIcon
              className={clsx('cursor-pointer mx-2 !w-5 !h-5 ml-4 mr-2.5', type === INFO ? 'text-[black]' : 'text-[white]')}
              onClick={() => handleDismiss()}
            />
          )}
        </CardActions>
      </Card>
    </SnackbarContent>
  );
});

NotificationSnackBar.displayName = 'notification';

export default NotificationSnackBar;
